<template>
  <div>
    <table-wrapper>
      <v-data-table-server
        :headers="tableHeaders"
        :items="fileFindings"
        :single-expand="false"
        v-model:expanded="expanded"
        :loading="fileFindingsLoading"
        :items-length="fileFindingsTotal"
        disable-pagination
        hide-default-footer
        item-value="filePath"
        show-expand
        fixed-header
        :hide-default-header="hideHeader"
        expand-icon="icon-chevron-down-xs"
      >
        <template #headers>
          <tr>
            <th class="w-10">
              <v-fab size="24" icon="expand-all-icon" color="primary" class="expand-all-icon">
                <v-icon
                  :class="{ rotated: expandAll }"
                  data-testid="file-findings-expand-all"
                  @click="handleExpandAllToggle"
                  icon="$chevronDown"
                ></v-icon>
              </v-fab>
            </th>
            <th class="w-80">
              {{ $t("ticketDetails.fullDetails.fileFindings.title") }}
            </th>
            <th class="text-right">
              {{ $t("ticketDetails.fullDetails.fileFindings.count") }}
            </th>
          </tr>
        </template>
        <template #item="{ item, index, isExpanded, toggleExpand, internalItem }">
          <tr :id="`${index}`" :class="{ 'row-no-border': isExpanded(internalItem) }">
            <td class="w-10" id="chevron-col">
              <v-icon
                :class="{ rotated: !isExpanded(internalItem) }"
                @click="toggleExpand(internalItem)"
                icon="$chevronUp"
              >
              </v-icon>
            </td>
            <td class="w-80 body2">
              <div class="findings-grid">
                <div class="findings-grid__label">{{ $t(`ticketDetails.fileName`) }}:</div>
                <div class="findings-grid__value">
                  <span class="subtitle2 mr-1">{{ item.fileName }}</span>
                  <span class="body2 text-indigo-medium"
                    >({{ convertSize(item.fileSize, 0) }})</span
                  >
                </div>
              </div>
            </td>
            <td class="text-right">
              <div class="body2">{{ item.findings.length }}</div>
              <div
                v-if="showLastSeen && item.lastSeenTime"
                class="caption text-indigo-medium"
                :data-testid="`file-findings-last-seen-${index}`"
              >
                {{
                  $t("ticketDetails.fullDetails.fileFindings.lastSeenOn", {
                    date: getFormattedDateTime(item.lastSeenTime, "ddd, ll"),
                  })
                }}
              </div>
            </td>
          </tr>
        </template>
        <template #expanded-row="{ columns, item }">
          <td class="w-10 expanded-row-border"></td>
          <td
            :colspan="columns.length"
            class="word-break-all white-space-normal expanded-row-border"
          >
            <div class="expandable-content pl-4">
              <div class="mr-4 instance-details">
                <findings-full-details
                  :findings-details="item"
                  :show-title="false"
                  :show-subtitle="false"
                />
              </div>
            </div>
          </td>
        </template>
      </v-data-table-server>
    </table-wrapper>
    <div v-if="findingsToLoad > 0" class="load-more-section">
      <div class="coro-link cursor-pointer" @click="handleLoadMore">
        {{ $t("ticketDetails.plusNFindings", { n: findingsToLoad }) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import FindingsFullDetails from "@/components/tickets/ticket-preview-details/full-details/FindingsFullDetails.vue";
import { defineComponent, type PropType, ref, watch, computed } from "vue";
import TableWrapper from "@/components/TableWrapper.vue";
import type { FileFinding } from "@/_store/tickets/ticket.module.ts";
import { convertSize } from "@coronet/cui";
import { useI18n } from "vue-i18n";
import { getFormattedDateTime } from "@/_helpers/date";

export default defineComponent({
  components: {
    TableWrapper,
    FindingsFullDetails,
  },
  props: {
    fileFindings: {
      type: Array as PropType<FileFinding[]>,
      required: true,
      default: () => [],
    },
    fileFindingsLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    fileFindingsTotal: {
      type: Number,
      required: true,
      default: 0,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    showLastSeen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["load-more"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const expanded = ref<string[]>([]);
    const expandAll = ref(false);

    const tableHeaders = [
      {
        title: "",
        key: "data-table-expand",
        sortable: false,
        headerProps: {
          "aria-label": i18n.t("ariaLabels.expanderColumn"),
        },
      },
      {
        key: "fileName",
        sortable: false,
      },
      { key: "totalFindings", sortable: false },
    ];

    const handleExpandAllToggle = () => {
      expandAll.value = !expandAll.value;
    };

    const handleLoadMore = () => {
      emit("load-more");
    };

    const findingsToLoad = computed(() => {
      return props.fileFindingsTotal - (props.fileFindings ?? []).length;
    });

    watch(expandAll, (newVal) => {
      if (newVal) {
        // must match the table's item-value, fileName is not unique.
        expanded.value = [...props.fileFindings.map((v) => v.filePath)];
      } else {
        expanded.value = [];
      }
    });

    return {
      tableHeaders,
      findingsToLoad,
      expanded,
      expandAll,
      handleExpandAllToggle,
      handleLoadMore,
      convertSize,
      getFormattedDateTime,
    };
  },
});
</script>

<style lang="scss" scoped>
.expand-all-icon {
  .v-icon::before {
    color: rgb(var(--v-theme-white)) !important;
  }
}

.load-more-section {
  border-left: 1px solid rgb(var(--v-theme-indigo-pale));
  border-right: 1px solid rgb(var(--v-theme-indigo-pale));
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.expanded-row-border {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale)) !important;
}

:deep(*) {
  table {
    tr {
      height: 64px;
    }

    thead > tr {
      height: 64px;
    }
  }
  .v-data-table__expanded__content {
    box-shadow: unset !important;
  }

  .v-btn--fab.v-size--small {
    height: 30px;
    width: 30px;
  }
  .v-btn {
    padding: 0 !important;
  }

  .v-table__wrapper {
    box-shadow: unset !important;
    border-radius: unset !important;
    border: 1px solid rgb(var(--v-theme-indigo-faint));
  }

  .full-details-wrapper {
    border: none !important;
  }

  .findings-wrapper {
    padding: 0 !important;
  }

  .row-no-border {
    td {
      border: none !important;
    }
  }
}
</style>
